/**
 * MapUIContext for managing UI state in the Needs Map
 */

import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { TopicNode } from '@/types/nodeTypes';
import { TopicEdge } from '@/types/edgeTypes';
import { Phase } from '@/types/phaseTypes';
import { 
  filterNodesBySearchTerm, 
  filterNodesByPhase, 
  filterNodesByImportance, 
  filterNodesByActive,
  filterEdgesByNodes,
  filterEdgesByActive
} from '@/lib/utils';
import { useMapState } from './MapStateContext';

/**
 * Filter visibility options
 */
export type FilterVisibility = 'all' | 'active-only' | 'inactive-only';

/**
 * MapUIContext type
 */
interface MapUIContextType {
  // State
  selectedNodeId: string | null;
  selectedEdgeId: string | null;
  searchTerm: string;
  selectedPhases: string[];
  minImportance: number;
  transcript: string;
  isProcessing: boolean;
  filterVisibility: FilterVisibility;
  animateLayout: boolean;
  
  // Operations
  selectNode: (nodeId: string | null) => void;
  selectEdge: (edgeId: string | null) => void;
  setSearchTerm: (term: string) => void;
  setSelectedPhases: (phaseIds: string[]) => void;
  setMinImportance: (importance: number) => void;
  setTranscript: (transcript: string) => void;
  setIsProcessing: (isProcessing: boolean) => void;
  setFilterVisibility: (visibility: FilterVisibility) => void;
  toggleAnimateLayout: () => void;
  
  // Derived data
  filteredNodes: TopicNode[];
  filteredEdges: TopicEdge[];
  selectedNode: TopicNode | null;
  selectedEdge: TopicEdge | null;
  phases: Phase[];
  
  // Helper properties
  showInactive: boolean; // Compatibility for older code
}

/**
 * Create MapUIContext
 */
const MapUIContext = createContext<MapUIContextType | undefined>(undefined);

/**
 * MapUIProvider props
 */
interface MapUIProviderProps {
  children: React.ReactNode;
}

/**
 * MapUIProvider component
 */
export const MapUIProvider: React.FC<MapUIProviderProps> = ({ 
  children 
}) => {
  // State
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedPhases, setSelectedPhases] = useState<string[]>([]);
  const [minImportance, setMinImportance] = useState<number>(1);
  const [transcript, setTranscript] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [filterVisibility, setFilterVisibility] = useState<FilterVisibility>('all');
  const [animateLayout, setAnimateLayout] = useState<boolean>(true);
  
  // Get map data from MapStateContext
  const { mapData } = useMapState();
  
  // トランスクリプトの初期設定
  useEffect(() => {
    if (mapData && mapData.transcript && !transcript) {
      setTranscript(mapData.transcript);
    }
  }, [mapData, transcript]);
  
  /**
   * Select a node
   */
  const selectNode = useCallback((nodeId: string | null) => {
    setSelectedNodeId(nodeId);
    // ノードを選択したら、エッジの選択を解除
    if (nodeId !== null) {
      setSelectedEdgeId(null);
    }
  }, []);
  
  /**
   * Select an edge
   */
  const selectEdge = useCallback((edgeId: string | null) => {
    setSelectedEdgeId(edgeId);
    // エッジを選択したら、ノードの選択を解除
    if (edgeId !== null) {
    }
  }, []);
  
  /**
   * Toggle animation state
   */
  const toggleAnimateLayout = useCallback(() => {
    setAnimateLayout(prev => !prev);
  }, []);
  
  /**
   * Get phases from mapData
   */
  const phases = useMemo(() => {
    // テンプレートから取得したフェーズ情報を使用
    return mapData?.phases || [];
  }, [mapData]);
  
  /**
   * Filter nodes based on search term, phases, and importance
   */
  const filteredNodes = useMemo(() => {
    if (!mapData) return [];
    
    let nodes = mapData.nodes;
    
    // Apply search filter
    if (searchTerm) {
      nodes = filterNodesBySearchTerm(nodes, searchTerm);
    }
    
    // Apply phase filter
    if (selectedPhases.length > 0) {
      nodes = filterNodesByPhase(nodes, selectedPhases);
    }
    
    // Apply importance filter
    if (minImportance > 1) {
      nodes = filterNodesByImportance(nodes, minImportance);
    }
    
    // Apply active/inactive filter
    if (filterVisibility === 'active-only') {
      nodes = filterNodesByActive(nodes, false); // Only show active
    } else if (filterVisibility === 'inactive-only') {
      nodes = nodes.filter(node => !node.active); // Only show inactive
    }
    
    return nodes;
  }, [mapData, searchTerm, selectedPhases, minImportance, filterVisibility]);
  
  /**
   * Filter edges based on filtered nodes and active state
   */
  const filteredEdges = useMemo(() => {
    if (!mapData) return [];
    
    const nodeIds = filteredNodes.map(node => node.id);
    let edges = filterEdgesByNodes(mapData.edges, nodeIds);
    
    // Apply active/inactive filter
    if (filterVisibility === 'active-only') {
      edges = filterEdgesByActive(edges, false); // Only show active
    } else if (filterVisibility === 'inactive-only') {
      edges = edges.filter(edge => !edge.active); // Only show inactive
    }
    
    return edges;
  }, [mapData, filteredNodes, filterVisibility]);
  
  /**
   * Get the selected node
   */
  const selectedNode = useMemo(() => {
    if (!selectedNodeId || !mapData) return null;
    return mapData.nodes.find(node => node.id === selectedNodeId) || null;
  }, [selectedNodeId, mapData]);
  
  /**
   * Get the selected edge
   */
  const selectedEdge = useMemo(() => {
    if (!selectedEdgeId || !mapData) return null;
    return mapData.edges.find(edge => edge.id === selectedEdgeId) || null;
  }, [selectedEdgeId, mapData]);
  
  /**
   * Backward compatibility for showInactive
   */
  const showInactive = useMemo(() => {
    return filterVisibility !== 'active-only';
  }, [filterVisibility]);
  
  // デバッグ用
  useEffect(() => {
    console.log("MapUIContext - filteredNodes:", filteredNodes.length);
    console.log("MapUIContext - filterVisibility:", filterVisibility);
  }, [filteredNodes, filterVisibility]);
  
  const value = {
    selectedNodeId,
    selectedEdgeId,
    searchTerm,
    selectedPhases,
    minImportance,
    transcript,
    isProcessing,
    filterVisibility,
    animateLayout,
    selectNode,
    selectEdge,
    setSearchTerm,
    setSelectedPhases,
    setMinImportance,
    setTranscript,
    setIsProcessing,
    setFilterVisibility,
    toggleAnimateLayout,
    filteredNodes,
    filteredEdges,
    selectedNode,
    selectedEdge,
    phases,
    showInactive // For compatibility
  };
  
  return (
    <MapUIContext.Provider value={value}>
      {children}
    </MapUIContext.Provider>
  );
};

/**
 * useMapUI hook
 */
export const useMapUI = (): MapUIContextType => {
  const context = useContext(MapUIContext);
  
  if (context === undefined) {
    throw new Error('useMapUI must be used within a MapUIProvider');
  }
  
  return context;
};